import axios from 'axios';
import { CONFIG } from 'variables/config';

// Shared by the seller's integration screens (formTemplates/list.tsx, start.tsx, installation.tsx,
// edit.tsx): the overview payload, the two snippets, per-tool install guides and small helpers.
// Wording follows notes/ux-integrations-vendeur.md (glossary §5).

export const authConfig = () => ({
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('token') },
});

/* ── GET /form-template/overview (leadrs-back services/integrationOverview.ts) ── */

export interface SiteOverview {
    hostname: string;
    pixelDetected: boolean;
    iframeViews: number;
    pages: number;
    verifiedPages: number;
    unclassifiedPages: number;
    leads30: number;
    sold30: number;
    lastLeadAt: string | null;
}

export interface ActivityOverview {
    industry: string;
    subIndustry: string;
    pages: number;
    verifiedPages: number;
    // Price this integration sells the activity at (its own, else the account's) and its origin.
    minimumPrice: number | null;
    priceSource: 'integration' | 'account' | null;
    accountPrice: number | null;
    integrationPrice: number | null;
}

export interface TemplateOverview {
    _id: string;
    name: string;
    isUniversal: boolean;
    source: string | null;
    industry: string | null;
    subIndustry: string | null;
    minimumPrice: number | null;
    // Fixed-activity integrations (Google Sheet, older API): account price of their activity.
    accountPrice: number | null;
    chatbotEnabled: boolean;
    sheetSyncEnabled: boolean;
    googleSheetId: string | null;
    sites: SiteOverview[];
    activities: ActivityOverview[];
    totals: { leads30: number; sold30: number; lastLeadAt: string | null };
}

export const fetchOverview = async (templateId?: string): Promise<TemplateOverview[]> => {
    const res = await axios.get(`${CONFIG.apiUrl}/form-template/overview`, {
        ...authConfig(),
        params: templateId ? { templateId } : undefined,
    });
    return res.data || [];
};

/** A site is installed once the embedded form shows on it, plus the Pixel unless the account doesn't need it. */
export const isSiteInstalled = (site: SiteOverview, pixelRequired: boolean) =>
    site.iframeViews > 0 && (!pixelRequired || site.pixelDetected);

export const pagesToCheck = (site: SiteOverview) => site.pages - site.verifiedPages;

/** Installation is done when every known site is installed and every activity has a price. */
export const isInstallationDone = (t: TemplateOverview, pixelRequired: boolean) =>
    t.sites.length > 0 &&
    t.sites.every((s) => isSiteInstalled(s, pixelRequired)) &&
    t.activities.length > 0 &&
    t.activities.every((a) => a.minimumPrice != null);

/* ── The seller's own "Mon formulaire Leadrs" (universal, not created by the MCP) ── */

export const isOwnUniversal = (t: { isUniversal?: boolean; source?: string | null }) => !!t.isUniversal && t.source !== 'mcp';

/** Returns the seller's own universal integration, creating it the first time. */
export const ensureOwnUniversalTemplate = async (): Promise<string> => {
    const res = await axios.get(`${CONFIG.apiUrl}/form-template`, authConfig());
    // A seller may have several (older flows created duplicates): new sites join the one in use.
    const existing = (res.data || [])
        .filter(isOwnUniversal)
        .sort((a: any, b: any) => (b.landingPageUrls?.length || 0) - (a.landingPageUrls?.length || 0))[0];
    if (existing) return existing._id;
    const created = await axios.post(`${CONFIG.apiUrl}/form-template`, { name: 'Mon formulaire Leadrs', isUniversal: true }, authConfig());
    return created.data._id;
};

/** A Sheet is really linked only with its id too: the sync job (leadrs-back services/gsheet.ts) skips the rest. */
export const hasLinkedSheet = (t: { sheetSyncEnabled?: boolean; googleSheetId?: string | null }) => !!t.sheetSyncEnabled && !!t.googleSheetId;

/** A Google Sheet import is always titled by its industry, whatever its stored name. */
export const sheetTitle = (t: { industry?: string | null }) => `Google Sheet · ${t.industry || 'activité à définir'}`;

export const DEFAULT_TEMPLATE_NAMES = ['Iframe', 'Iframe universelle', 'Mon formulaire Leadrs'];
export const integrationTitle = (t: { name?: string; isUniversal?: boolean; sheetSyncEnabled?: boolean; googleSheetId?: string | null; industry?: string | null }) =>
    !t.isUniversal && hasLinkedSheet(t)
        ? sheetTitle(t)
        : t.isUniversal && (!t.name || DEFAULT_TEMPLATE_NAMES.includes(t.name))
        ? 'Mon formulaire Leadrs'
        : t.name || 'Intégration';

/* ── Snippets ── */

export const PIXEL_SCRIPT_SNIPPET = '<script src="https://api.leadrs.co/leadrs-pixel/pixel.js" async></script>';
export const leadFormUrlFor = (templateId: string) => `${window.location.origin}/public/lead-form?templateId=${templateId}`;
export const iframeSnippetFor = (templateId: string) =>
    `<iframe src="${leadFormUrlFor(templateId)}" width="100%" height="600" frameborder="0" allowtransparency="true"></iframe>`;
export const TEST_LEAD_EMAIL = 'testing@leadrs.co';

/* ── Where to paste the codes, per site builder ── */

export type SiteTool = 'wordpress' | 'wix' | 'webflow' | 'shopify' | 'gtm' | 'code';

export const SITE_TOOLS: { key: SiteTool; label: string; pixel: string[]; form: string[] }[] = [
    {
        key: 'wordpress',
        label: 'WordPress',
        pixel: [
            "Installez une extension d'insertion de code (par exemple WPCode).",
            'Ajoutez un extrait « En-tête du site » et collez le code ①.',
            "Enregistrez : il s'applique à toutes les pages.",
        ],
        form: [
            "Ouvrez la page dans l'éditeur.",
            'Ajoutez un bloc « HTML personnalisé » là où doit apparaître le formulaire.',
            'Collez le code ② et mettez la page à jour.',
        ],
    },
    {
        key: 'wix',
        label: 'Wix',
        pixel: ['Paramètres du site → Code personnalisé → Ajouter un code.', 'Collez le code ①, choisissez « Toutes les pages » et « En-tête ».'],
        form: ["Dans l'éditeur : Ajouter → Intégrer du code → Intégrer HTML.", 'Collez le code ② dans le bloc, placez-le et publiez.'],
    },
    {
        key: 'webflow',
        label: 'Webflow',
        pixel: ['Site settings → Custom code → Head code.', 'Collez le code ① et publiez le site.'],
        form: ['Ajoutez un élément « Embed » dans la page.', 'Collez le code ② et publiez.'],
    },
    {
        key: 'shopify',
        label: 'Shopify',
        pixel: ['Boutique en ligne → Thèmes → Modifier le code.', 'Dans theme.liquid, collez le code ① juste avant </head>.'],
        form: ["Dans l'éditeur de thème, ajoutez une section « Liquid personnalisé ».", 'Collez le code ② et enregistrez.'],
    },
    {
        key: 'gtm',
        label: 'Google Tag Manager',
        pixel: ['Nouvelle balise → HTML personnalisé, collez le code ①.', 'Déclencheur : All Pages. Publiez le conteneur.'],
        form: [
            'Le formulaire se colle dans la page elle-même, pas via Google Tag Manager.',
            "Ajoutez le code ② avec l'éditeur de votre site, là où il doit apparaître.",
        ],
    },
    {
        key: 'code',
        label: 'Autre / code',
        pixel: ['Collez le code ① juste avant </head>, sur toutes les pages (dans votre gabarit commun).'],
        form: ["Collez le code ② dans le HTML de la page, à l'endroit du formulaire.", 'Le même code sert sur toutes les pages : ne le modifiez pas.'],
    },
];

/* ── Display helpers ── */

export const timeAgo = (iso: string | null | undefined): string => {
    if (!iso) return '—';
    const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    if (minutes < 1) return "à l'instant";
    if (minutes < 60) return `il y a ${minutes} min`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `il y a ${hours} h`;
    const days = Math.round(hours / 24);
    return days === 1 ? 'hier' : `il y a ${days} j`;
};

export const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

/** Seller-side price from a buyer price, same commission rule as the editor (edit.tsx). */
export const buyerToSellerPrice = (buyerPrice: number, user: any) =>
    user?.commissionFixedPrice != null ? buyerPrice - user.commissionFixedPrice : buyerPrice / (1 + (user?.commission ?? 15) / 100);

export const normalizeSubIndustry = (value?: string | null) =>
    (value || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim();

export interface PurchaseOrderVolume {
    subIndustry: string;
    orderCount?: number;
    departments: string[];
    minPricePerLead?: number;
}

export const fetchPurchaseOrderVolumes = async (): Promise<PurchaseOrderVolume[]> => {
    const res = await axios.get(`${CONFIG.apiUrl}/purchase-order/volumes`, authConfig());
    return res.data || [];
};

export const marketFor = (volumes: PurchaseOrderVolume[], subIndustry: string | null | undefined, user: any) => {
    const v = volumes.find((x) => normalizeSubIndustry(x.subIndustry) === normalizeSubIndustry(subIndustry));
    if (!v) return { orders: 0, sellerPrice: null as number | null };
    return {
        orders: v.orderCount ?? 0,
        sellerPrice: v.minPricePerLead != null ? Math.max(0, Math.round(buyerToSellerPrice(v.minPricePerLead, user))) : null,
    };
};

/* ── Account prices ("Mes prix", GET/PUT /seller-industry-price) ── */

export interface AccountPrice {
    _id?: string;
    industry: string;
    subIndustry?: string;
    minimumPrice: number;
}

export const fetchAccountPrices = async (): Promise<AccountPrice[]> => {
    const res = await axios.get(`${CONFIG.apiUrl}/seller-industry-price`, authConfig());
    return res.data || [];
};

export const accountPriceFor = (prices: AccountPrice[], industry?: string | null, subIndustry?: string | null) =>
    prices.find((p) => p.industry === industry && (p.subIndustry || '') === (subIndustry || ''))?.minimumPrice;

export const PRICES_PATH = '/integrations/prices';

/* ── Pages of a universal integration (GET /form-template/:id/pages) ── */

export type ActivitySource = 'page' | 'site' | 'default' | 'pixel' | null;

export interface IntegrationPage {
    url: string;
    hostname: string;
    industry: string | null;
    subIndustry: string | null;
    // 'page': chosen/checked by the seller · 'site': the site's default activity · 'default': set by
    // the AI agent for the whole integration · 'pixel': detected, to check · null: unknown.
    activitySource: ActivitySource;
    verified: boolean;
    views: number;
    leads30: number;
    sold30: number;
    lastLeadAt: string | null;
    lastVisitAt: string | null;
}

export interface IntegrationSitePages {
    hostname: string;
    pages: number;
    defaultActivity: { industry: string; subIndustry: string | null } | null;
}

export const fetchPages = async (templateId: string): Promise<{ pages: IntegrationPage[]; sites: IntegrationSitePages[] }> => {
    const res = await axios.get(`${CONFIG.apiUrl}/form-template/${templateId}/pages`, authConfig());
    return { pages: res.data?.pages || [], sites: res.data?.sites || [] };
};

export const activityLabel = (a: { industry?: string | null; subIndustry?: string | null }) => a.subIndustry || a.industry || '';
